import { Fragment, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useHistory } from 'react-router-dom'
import PageModal from '../common/PageModal'
import ConfirmationModal from '../common/ConfirmationModal'
import Dropdown from '../common/Dropdown'
import ToggleSwitch from '../common/ToggleSwitch'
import SimpleButton from '../common/SimpleButton'
import { toast } from '../common/Toast'
import S3Image from '../common/S3Image'
import { CompaniesIcon, ExpandLessIcon, ExpandMoreIcon } from '../../icons'
import {
  loadUserDevices,
  loadUserSubscriptions,
  loadUserPermissions,
  partiallyUpdateUser,
  clearUserDevices,
  clearUserSubscriptions,
  clearUserPermissions,
} from '../../actions/users'
import { reactivateSubscription, cancelSubscription } from '../../actions/adminOrderHistory'
import countries from '../../utils/countries.json'
import { postSendResetPasswordEmail, postVerifyUser } from '../../utils/requests/usersAPI'
import { loadUserRolesOptions } from '../../actions/accountsManagement'
import { clearUserBranches, loadBranchesByCompanyId } from '../../actions/branches'
import { capitalizeFirstLetter, formatPhone, getImageByModel } from '../../utils/functions'
import { no_image_200x200 } from '../../images'
import languages, { getLocaleFromIso639_2, getLanguageFromLocale } from '../../utils/languages'
import { deleteOnlineAccessAssociation, putOnlineAccessAssociation } from '../../utils/requests/productsAPI'
import { hasAdminModulesPermission, hasCorporateAdminModulesPermission, productsWritePermission } from '../../utils/permissionValidation'
import { checkPermission, hasEndUserSiteAccess, usersDeletePermission, usersWritePermission } from '../../utils/permissionValidation'

const ViewEditUser = (props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { isEditMode, selectedUser, statuses, deleteUser, confirmUser, closeModal, isLoading, showSpinner, hideSpinner, handleNavigationWithState } = props
  const [userDevices, setUserDevices] = useState([])
  const [userSubscriptions, setUserSubscriptions] = useState([])
  const [accountsWithRoles, setAccountsWithRoles] = useState([])
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [language, setLanguage] = useState(null)
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState('')
  const [company, setCompany] = useState('')
  const [companyAccountType, setCompanyAccountType] = useState('')
  const [organization, setOrganization] = useState('')
  const [branch, setBranch] = useState('')
  const [account, setAccount] = useState(null)
  const [role, setRole] = useState(null)
  const [isColteneAdmin, setIsColteneAdmin] = useState(false)
  const [isCorporateAdmin, setIsCorporateAdmin] = useState(false)
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false)
  const [isCompanyMember, setIsCompanyMember] = useState(false)
  // const [hasSiteAccess, setHasSiteAccess] = useState(false)
  // const [hasDealerTechPermissions, setHasDealerTechPermissions] = useState(false)
  const [hasEndUserSitePermissions, setHasEndUserSitePermissions] = useState(false)
  const [status, setStatus] = useState('')
  const [initialLanguage, setInitialLanguage] = useState(null)
  const [initialPhone, setInitialPhone] = useState('')
  const [initialCountry, setInitialCountry] = useState('')
  const [initialAccount, setInitialAccount] = useState(null)
  const [initialRole, setInitialRole] = useState(null)
  // const [initialHasSiteAccess, setInitialHasSiteAccess] = useState(false)
  const [initialStatus, setInitialStatus] = useState('')
  const [isUnconfirmedUser, setIsUnconfirmedUser] = useState(false)
  const [areDevicesExpanded, setAreDevicesExpanded] = useState(false)
  const [areSubscriptionsExpanded, setAreSubscriptionsExpanded] = useState(false)
  const [areBranchesExpanded, setAreBranchesExpanded] = useState(false)
  const [isEditEnabled, setIsEditEnabled] = useState(false)
  const [showPasswordResetConfirmModal, setShowPasswordResetConfirmModal] = useState(false)
  const [showVerifyUserModal, setShowVerifyUserModal] = useState(false)
  const [isFirstDevicesLoad, setIsFirstDevicesLoad] = useState(true)
  const [isFirstSubscriptionsLoad, setIsFirstSubscriptionsLoad] = useState(true)
  const [showChangeSubscriptionConfirmationModal, setShowChangeSubscriptionConfirmationModal] = useState(false)
  const [subscriptionToChange, setSubscriptionToChange] = useState(null)
  const [haveError, setHaveError] = useState({})
  const companyAdminRoles = ['Dentist Admin', 'Dealer Admin', 'DSO']
  const isAdmin = props?.authedUser?.userGroup === 'admin'

  useEffect(() => {
    if (props.userRolesOptions.length === 0) {
      props.actions.loadUserRolesOptions()
    }
  }, [])

  useEffect(() => {
    const user = props.users.find((user) => user.uuid === selectedUser.uuid)
    if (user) {
      setFirstName(user.firstName)
      setLastName(user.lastName)
      setEmail(user.email)
      setLanguage(getLocaleFromIso639_2(user.lang))
      setPhone(formatPhone(user.phone))
      setCountry(user.country)
      setCompany(user.company)
      setOrganization(user.companyName)
      setBranch(user.branchName)
      setAccount(user.account?.id)
      setRole(user.role?.id)
      // setHasSiteAccess(user.dealerTechAccess)
      setStatus(user.status)
      // setHasDealerTechPermissions(user.role?.name?.toLowerCase() === 'dealer technician')
      setIsUnconfirmedUser(user.confirmationStatus?.toLowerCase() === 'unconfirmed')
      setInitialLanguage(getLocaleFromIso639_2(user.lang))
      setInitialPhone(formatPhone(user.phone))
      setInitialCountry(user.country)
      setInitialAccount(user.account?.id)
      setInitialRole(user.role?.id)
      // setInitialHasSiteAccess(user.dealerTechAccess)
      setInitialStatus(user.status)
    }

    props.actions.loadUserPermissions(user.uuid)
  }, [props.users])

  useEffect(() => {
    const user = props.users.find((user) => user.uuid === selectedUser.uuid)
    if (user && props.endUserPermissions.length > 0) {
      const isCorporate = hasCorporateAdminModulesPermission(props.endUserPermissions)
      const hasCompanyAdminRole = companyAdminRoles.some((adminRole) => user.role?.name?.toLowerCase()?.includes(adminRole.toLowerCase()))
      const hasCompany = user.companyAccountType !== 'individual'

      setIsCompanyAdmin(hasCompanyAdminRole)
      setIsCompanyMember(hasCompany)

      const companyType = isCorporate ? 'corporate_admin' : hasCompany ? (hasCompanyAdminRole ? 'organization_admin' : 'organization_member') : 'individual'

      setCompanyAccountType(companyType)
    }
  }, [props.users, props.endUserPermissions])

  useEffect(() => {
    let isActive = true

    const run = async () => {
      if (!isEditEnabled || props.userRolesOptions.length === 0 || props.accounts.length === 0) {
        if (isActive) setAccountsWithRoles([])
        return
      }

      const activeEligibleAccounts = props.accounts.filter((account) => account.isEnabled)
      const assignableRoles = props.userRolesOptions.filter((role) => {
        const roleName = role.name?.toLowerCase() || ''
        if (!props.isCurrentAdmin && (roleName === 'admin' || roleName === 'super admin')) {
          return false
        }
        return !companyAdminRoles.some((adminRole) => roleName.includes(adminRole.toLowerCase()))
      })

      const accountsWithRoles = activeEligibleAccounts
        .map((account) => {
          const mappedRoles = assignableRoles.filter((role) => role.accountId === account.id).map((role) => ({ id: role.id, name: role.name }))
          if (mappedRoles.length === 0) return null
          return {
            id: account.id,
            name: account.name,
            children: mappedRoles,
          }
        })
        .filter(Boolean)

      if (!isActive) return
      setAccountsWithRoles(accountsWithRoles)
    }

    run()
    return () => {
      isActive = false
    }
  }, [isEditEnabled, props.userRolesOptions, props.accounts])

  useEffect(() => {
    if (props.userDevices.length > 0) {
      setUserDevices(props.userDevices)
    }
  }, [props.userDevices])

  useEffect(() => {
    if (props.userSubscriptions.length > 0) {
      setUserSubscriptions(props.userSubscriptions)
    }
  }, [props.userSubscriptions])

  useEffect(() => {
    if (props.endUserPermissions.length > 0) {
      // const includesDealerTechPermissions = hasDealerSiteAccess(props.endUserPermissions)
      //TODO: Use permissions once implemented on BE
      //setHasDealerTechPermissions(includesDealerTechPermissions)
      const includesCorporateAdminPermissions = hasCorporateAdminModulesPermission(props.endUserPermissions)
      setIsCorporateAdmin(includesCorporateAdminPermissions)

      const includesColteneAdminPermissions = hasAdminModulesPermission(props.endUserPermissions)
      setIsColteneAdmin(includesColteneAdminPermissions)

      const includesEndUserSitePermissions = hasEndUserSiteAccess(props.endUserPermissions)
      setHasEndUserSitePermissions(includesEndUserSitePermissions)
    }
  }, [props.endUserPermissions])

  useEffect(() => {
    if (hasEndUserSitePermissions) {
      props.actions.loadUserDevices(selectedUser.uuid).then(() => setIsFirstDevicesLoad(false))
      props.actions.loadUserSubscriptions(selectedUser.uuid).then(() => setIsFirstSubscriptionsLoad(false))
    }
  }, [hasEndUserSitePermissions])

  useEffect(() => {
    if (isCorporateAdmin) {
      props.actions.loadBranchesByCompanyId(selectedUser.companyId)
    }
  }, [isCorporateAdmin])

  useEffect(() => {
    setIsEditEnabled(isEditMode)
  }, [isEditMode])

  const userDetailsHeaderContent = () => {
    return (
      <div className="status-wrapper">
        <p className="input-option">{t('status')}</p>
        {renderUserStatus(status)}
      </div>
    )
  }

  const userDetailsFooterContent = () => {
    return (
      <>
        {isEditEnabled ? (
          <>
            <SimpleButton className="cancel-button is-edit-button" onClick={() => handleOnClose()}>
              {t('cancel')}
            </SimpleButton>
            <SimpleButton
              className="submit-button is-edit-button"
              disabled={isDisabledSaveButton() || isLoading}
              onClick={() => handleUpdateUser()}
              requiredPermission={usersWritePermission}
            >
              {t('save')}
            </SimpleButton>
          </>
        ) : (
          <>
            <SimpleButton
              className="action-delete-button"
              onClick={() => deleteUser(selectedUser.uuid)}
              disabled={status !== statuses[2] && !isUnconfirmedUser}
              requiredPermission={usersDeletePermission}
            >
              {t('delete')}
            </SimpleButton>
            {isUnconfirmedUser ? (
              <>
                <SimpleButton className="cancel-button" onClick={() => setShowVerifyUserModal(true)} requiredPermission={usersWritePermission}>
                  {t('users-management.verify-user')}
                </SimpleButton>

                <SimpleButton className="cancel-button" onClick={() => setShowPasswordResetConfirmModal(true)} requiredPermission={usersWritePermission}>
                  {t('account_context.reset_password')}
                </SimpleButton>

                <SimpleButton
                  className="submit-button"
                  onClick={() => confirmUser(selectedUser.uuid)}
                  disabled={isLoading}
                  requiredPermission={usersWritePermission}
                >
                  {t('confirm')}
                </SimpleButton>
              </>
            ) : (
              <>
                <SimpleButton className="cancel-button" onClick={() => setShowPasswordResetConfirmModal(true)} requiredPermission={usersWritePermission}>
                  {t('account_context.reset_password')}
                </SimpleButton>
                <SimpleButton
                  className="action-button"
                  onClick={() => {
                    setIsEditEnabled(true)
                    setAreDevicesExpanded(false)
                    setAreSubscriptionsExpanded(false)
                  }}
                  requiredPermission={usersWritePermission}
                >
                  {t('edit')}
                </SimpleButton>
              </>
            )}
          </>
        )}
      </>
    )
  }

  const isDisabledSaveButton = () => {
    return (
      language === initialLanguage &&
      phone === initialPhone &&
      country === initialCountry &&
      account === initialAccount &&
      role === initialRole &&
      // hasSiteAccess === initialHasSiteAccess &&
      status === initialStatus
    )
  }

  const selectCountry = (country) => {
    setCountry(country)
  }

  const selectLanguage = (language) => {
    setLanguage(language)
  }

  const selectAccount = (accountId) => {
    if (account !== accountId) {
      setRole(null)
    }

    setAccount(accountId)
  }

  const selectRole = (roleId, roleName) => {
    setRole(roleId)

    // setHasDealerTechPermissions(roleName?.toLowerCase() === 'dealer technician')
  }

  const isValidInput = () => {
    setHaveError({})

    if (!role) {
      toast.error(t('error.role'))
      setHaveError({ ...haveError, role: true })
      return false
    }

    const phonePattern = new RegExp(/^\d*$/)
    if (!phonePattern.test(formatPhone(phone).trim().replaceAll('-', ''))) {
      toast.error(t('error.phone'))
      setHaveError({ ...haveError, phone: true })
      return false
    }

    return true
  }

  const handleUpdateUser = () => {
    if (!isValidInput()) {
      return
    }

    let bodyParams = {
      locale: language,
      phone: formatPhone(phone).replaceAll('-', ''),
      country: country,
      roleId: role,
    }

    // if (hasDealerTechPermissions) {
    //   bodyParams = { ...bodyParams, dealerTechAccess: hasSiteAccess }
    // }

    props.actions.partiallyUpdateUser(selectedUser.uuid, bodyParams)

    handleOnClose()
  }

  const handleChangeStatusUser = () => {
    if (isLoading || !checkPermission(props.userPermissions, usersWritePermission)) {
      return
    }

    let newStatus = statuses[0]
    if (status === newStatus) {
      newStatus = statuses[2]
    }

    props.actions.partiallyUpdateUser(selectedUser.uuid, { status: newStatus })
  }

  const renderUserStatus = (status) => {
    switch (status) {
      case statuses[0]:
        return (
          <div className="status">
            <div onClick={() => handleChangeStatusUser()}>
              <ToggleSwitch checked={true} />
            </div>
            <p className="active">{t('enabled')}</p>
          </div>
        )
      case statuses[1]:
        return (
          <div className="status">
            <div onClick={() => handleChangeStatusUser()}>
              <ToggleSwitch checked={false} />
            </div>
            <p className="pending">{t('pending')}</p>
          </div>
        )
      case statuses[2]:
        return (
          <div className="status">
            <div onClick={() => handleChangeStatusUser()}>
              <ToggleSwitch checked={false} />
            </div>
            <p className="inactive">{t('disabled')}</p>
          </div>
        )
      default:
        return null
    }
  }

  const handleUserResetPassword = () => {
    setShowPasswordResetConfirmModal(false)
    showSpinner()

    postSendResetPasswordEmail(selectedUser.uuid)
      .then(() => {
        hideSpinner()
        toast.success(t('users-management.email-sent-succesfully'))
      })
      .catch(() => {
        hideSpinner()
        toast.error(t('error.something_wrong'))
      })
  }

  const handleVerifyUser = () => {
    setShowVerifyUserModal(false)
    showSpinner()

    postVerifyUser(selectedUser.uuid, selectedUser.email)
      .then(() => {
        hideSpinner()
        toast.success(t('users-management.verified-success'))
      })
      .catch(() => {
        hideSpinner()
        toast.error(t('error.something_wrong'))
      })
  }

  const handleChangeAssociation = (serialNumber, targetUserEmail, associate) => {
    if (isLoading) {
      return
    }

    showSpinner()

    const changeAssociationRequestBody = {
      serial_number: serialNumber,
      email: targetUserEmail,
      action_by: props.adminEmail,
    }

    let changeAssociationPromise

    if (associate) {
      changeAssociationPromise = putOnlineAccessAssociation(changeAssociationRequestBody)
    } else {
      changeAssociationPromise = deleteOnlineAccessAssociation(changeAssociationRequestBody)
    }

    changeAssociationPromise
      .then((result) => {
        if (result[0]?.status === 1) {
          const updatedDevices = userDevices.map((device) => {
            if (device.serialNumber === serialNumber) {
              return { ...device, registered: associate }
            }
            return device
          })

          setUserDevices(updatedDevices)
        }

        if (result[0]?.status === 0) {
          toast.error(t([`online-access.${result[0].error?.code}`, 'error.something_wrong']))
        }

        hideSpinner()
      })
      .catch(() => {
        toast.error(t('error.failure_msg'))

        hideSpinner()
      })
  }

  const handleChangeSubscription = () => {
    if (isLoading || !subscriptionToChange) {
      return
    }

    showSpinner()

    let subscriptionPromise

    if (subscriptionToChange.cancelAtPeriodEnd) {
      subscriptionPromise = props.actions.reactivateSubscription(subscriptionToChange.subscriptionId, selectedUser.uuid)
    } else {
      subscriptionPromise = props.actions.cancelSubscription(subscriptionToChange.subscriptionId, selectedUser.uuid)
    }

    subscriptionPromise
      .then((response) => {
        const updatedSubscriptions = userSubscriptions.map((subscription) => {
          if (subscription.id === subscriptionToChange.subscriptionId) {
            return { ...subscription, cancelAtPeriodEnd: !subscriptionToChange.cancelAtPeriodEnd, status: response?.value?.status }
          }
          return subscription
        })

        setUserSubscriptions(updatedSubscriptions)

        hideSpinner()
      })
      .catch((e) => {
        toast.error(t([`error.${e?.response?.data?.code}`, 'error.something_wrong']))

        hideSpinner()
      })
  }

  const navigateToProductPage = (serialNumber) => {
    handleNavigationWithState(selectedUser.uuid)

    history.push({
      pathname: `/products/${serialNumber}`,
      state: {
        withBackNavigation: true,
        activeTab: 3,
      },
    })
  }

  const handleOnClose = () => {
    props.actions.clearUserDevices()
    props.actions.clearUserSubscriptions()
    props.actions.clearUserPermissions()
    props.actions.clearUserBranches()

    if (closeModal) {
      closeModal()
    }
  }

  return (
    <Fragment>
      <PageModal
        toggle
        onToggle={() => handleOnClose()}
        title={t('details')}
        headerContent={!isUnconfirmedUser && userDetailsHeaderContent()}
        footerContent={userDetailsFooterContent()}
      >
        <div className="user-details-modal-content">
          {isUnconfirmedUser && <div className="unconfirmed-user-disclaimer">{t('users-management.user_not_confirmed')}</div>}
          <div className="user-details-item-wrapper">
            <div className="user-details-item">
              <div className="title">
                <p>{t('first_name')}</p>
              </div>
              {isEditEnabled ? (
                <input disabled type="text" className="input-item" value={firstName ?? ''} onChange={(e) => setFirstName(e.target.value)}></input>
              ) : (
                <div className="value">
                  <p>{firstName}</p>
                </div>
              )}
            </div>
            <div className="user-details-item">
              <div className="title">
                <p>{t('last_name')}</p>
              </div>
              {isEditEnabled ? (
                <input disabled type="text" className="input-item" value={lastName ?? ''} onChange={(e) => setLastName(e.target.value)}></input>
              ) : (
                <div className="value">
                  <p>{lastName}</p>
                </div>
              )}
            </div>
          </div>
          <div className="user-details-item-wrapper">
            <div className="user-details-item">
              <div className="title">
                <p>{t('email')}</p>
              </div>
              {isEditEnabled ? (
                <input disabled type="text" className="input-item" value={email ?? ''} onChange={(e) => setEmail(e.target.value)}></input>
              ) : (
                <div className="value">
                  <p>{email}</p>
                </div>
              )}
            </div>
            <div className="user-details-item">
              <div className="title">
                <p>{t('language')}</p>
              </div>
              {isEditEnabled ? (
                <Dropdown
                  className="dropdown-input"
                  placeholder={t('users-management.select-language')}
                  options={Object.keys(languages).map((languageOption) => ({
                    key: languageOption,
                    value: t([`${getLanguageFromLocale(languageOption)}`, '']),
                  }))}
                  initialSelectedKey={language}
                  onChange={(language) => selectLanguage(language.key)}
                />
              ) : (
                <div className="value">
                  <p>{t([`${getLanguageFromLocale(language)}`, ''])}</p>
                </div>
              )}
            </div>
          </div>
          <div className="user-details-item-wrapper">
            <div className="user-details-item">
              <div className="title">
                <p>{t('phone')}</p>
              </div>
              {isEditEnabled ? (
                <input
                  type="text"
                  className={'input-item' + (haveError['phone'] ? ' has-error' : '')}
                  value={formatPhone(phone) ?? ''}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                ></input>
              ) : (
                <div className="value">
                  <p>{formatPhone(phone)}</p>
                </div>
              )}
            </div>
            <div className="user-details-item">
              <div className="title">
                <p>{t('users-management.company_account_type')}</p>
              </div>
              {isEditEnabled ? (
                <input disabled type="text" className="input-item" value={t([`users-management.${companyAccountType}`, companyAccountType])} />
              ) : (
                <div className="value">
                  <p>{t([`users-management.${companyAccountType}`, companyAccountType])}</p>
                </div>
              )}
            </div>
          </div>
          {isCompanyMember && (
            <div className="user-details-item-wrapper">
              <div className="user-details-item">
                <div className="title">
                  <p>{t('organization')}</p>
                </div>
                {isEditEnabled ? (
                  <input disabled type="text" className="input-item" value={organization || ''} />
                ) : (
                  <div className="value">
                    <p>{organization}</p>
                  </div>
                )}
              </div>
              {!isCorporateAdmin && (
                <div className="user-details-item">
                  <div className="title">
                    <p>{t('branch')}</p>
                  </div>
                  {isEditEnabled ? (
                    <input disabled type="text" className="input-item" value={branch || ''} />
                  ) : (
                    <div className="value">
                      <p>{branch}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className={'user-details-item-wrapper' + (isCompanyMember ? ' single-item' : '')}>
            <div className="user-details-item">
              <div className="title">
                <p>{t('country')}</p>
              </div>
              {isEditEnabled ? (
                <Dropdown
                  className="dropdown-input"
                  placeholder={t('users-management.select-country')}
                  options={countries?.map((countryOption) => ({ key: countryOption['code'], value: countryOption['name'] }))}
                  initialSelectedKey={country}
                  onChange={(country) => selectCountry(country.key)}
                  withFiltering
                />
              ) : (
                <div className="value">
                  <p>{country}</p>
                </div>
              )}
            </div>
            <div className={'user-details-item' + (isCompanyMember ? ' mobile-hidden' : '')}>
              {!isCompanyMember && (
                <>
                  <div className="title">
                    <p>{t('company')}</p>
                  </div>
                  {isEditEnabled ? (
                    <input disabled type="text" className="input-item" value={company || ''} />
                  ) : (
                    <div className="value">
                      <p>{company}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="user-details-item-wrapper with-big-margin-top">
            <div className="user-details-item">
              <div className="title">
                <p>{t('account')}</p>
              </div>
              {isEditEnabled && !isCompanyAdmin && !isCorporateAdmin && !(!props.isCurrentAdmin && isColteneAdmin) ? (
                <Dropdown
                  className="dropdown-input"
                  placeholder={t('users-management.select-account')}
                  options={accountsWithRoles?.map((accountOption) => ({ key: accountOption.id, value: accountOption.name }))}
                  initialSelectedKey={account}
                  onChange={(account) => selectAccount(account.key)}
                />
              ) : (
                <div className="value">
                  <p>{selectedUser.account?.name}</p>
                </div>
              )}
            </div>
            <div className="user-details-item">
              <div className="title">
                <p>{t('role')}</p>
              </div>
              {isEditEnabled && !isCompanyAdmin && !isCorporateAdmin && !(!props.isCurrentAdmin && isColteneAdmin) ? (
                <Dropdown
                  className="dropdown-input"
                  placeholder={capitalizeFirstLetter(t('select_role'))}
                  options={(accountsWithRoles?.find((a) => a.id === account)?.children || [])
                    .map((roleOption) => ({ key: roleOption.id, value: roleOption.name }))
                    .sort((a, b) => a.value.localeCompare(b.value))}
                  initialSelectedKey={role}
                  onChange={(role) => selectRole(role.key, role.value)}
                  hasError={haveError['role']}
                />
              ) : (
                <div className="value">
                  <p>{selectedUser.role?.name}</p>
                </div>
              )}
            </div>
          </div>
          {/* {hasDealerTechPermissions && (
            <div className="user-details-item-wrapper">
              <div className="user-details-item">
                <div className="title">
                  <p>{t('users-management.dealer-full-access')}</p>
                </div>
                {isEditEnabled ? (
                  <div className="d-flex flex-align-center">
                    <ToggleSwitch checked={hasSiteAccess} onChange={() => setHasSiteAccess((prevValue) => !prevValue)} />
                    <p className="with-margin-left">{hasSiteAccess ? t('yes') : t('no')}</p>
                  </div>
                ) : (
                  <div className="value">
                    <p>{hasSiteAccess ? t('yes') : t('no')}</p>
                  </div>
                )}
              </div>
            </div>
          )} */}
          {!isEditEnabled && hasEndUserSitePermissions && (
            <div className="units-subscriptions-wrapper with-big-margin-top">
              {props.userBranches?.length > 0 && (
                <>
                  <div className="separator" />
                  <div className="branches-header" onClick={() => setAreBranchesExpanded((prev) => !prev)}>
                    <div className="header-title">
                      <div className="title">{t('organization_context.branches')}</div>
                      <div className="subtitle">
                        <span>{`${t('users-management.total_branches')}: `}</span>
                        <span className="highlight">{props.userBranches.length}</span>
                      </div>
                    </div>
                    <div className="expand-button">{areBranchesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</div>
                  </div>

                  {areBranchesExpanded &&
                    props.userBranches.map((branch, index) => (
                      <div
                        className={
                          'item-wrapper' + (index === 0 ? ' first-item-wrapper' : '') + (index === props.userBranches.length - 1 ? ' last-item-wrapper' : '')
                        }
                        key={branch.id}
                      >
                        <S3Image imageKey={branch.imageUrl} fallbackIcon={<CompaniesIcon />} />
                        <div className="item-title-wrapper full-width">
                          <p className="title">{branch.branchName}</p>
                          <div>
                            <span className="label">{`${t('organization_context.admin_email')}: `}</span>
                            <span className="subtitle">{branch.email}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  <div className="separator" />
                </>
              )}
              <div className="separator" />
              <div className="units-header" onClick={() => setAreDevicesExpanded((prev) => !prev)}>
                <div className="header-title">
                  <div className="title">{t('users-management.coltene-products')}</div>
                  <div className="subtitle">
                    {!isFirstDevicesLoad && !isFirstSubscriptionsLoad && (
                      <>
                        <span>{`${t('users-management.total-products')}: `}</span>
                        <span className="highlight">{userDevices.length}</span>
                        <span>{` ${t('users-management.registered-products')}: `}</span>
                        <span className="highlight">{userDevices.filter((d) => d.registered)?.length}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="expand-button">{areDevicesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</div>
              </div>
              {areDevicesExpanded &&
                userDevices.map((userDevice, index) => {
                  const normalizedModelName = userDevice.modelName?.toLowerCase().replace(/[-_]+/g, ' ') || ''
                  const isBiosonic = normalizedModelName.includes('biosonic')
                  const isCanalProJeniEvo = normalizedModelName.includes('canalpro jeni evo')
                  const isCanalProJeni = normalizedModelName.includes('canalpro jeni') && !isCanalProJeniEvo
                  const shouldShowDeviceStatusWrapper = !(isBiosonic || isCanalProJeni || (!isAdmin && isCanalProJeniEvo))

                  return (
                    <div
                      className={'item-wrapper' + (index === 0 ? ' first-item-wrapper' : '') + (index === userDevices.length - 1 ? ' last-item-wrapper' : '')}
                      key={index}
                    >
                      <div className="item-image-wrapper">
                        <img src={getImageByModel(userDevice.modelName?.replace(/-/g, ' '))} className="item-image" alt="product" />
                      </div>
                      <div className="item-title-wrapper">
                        <p className="title">{userDevice.modelName?.replace(/-/g, ' ')}</p>
                        <SimpleButton onClick={() => navigateToProductPage(userDevice.serialNumber)} isLabel requiredPermission={productsWritePermission}>
                          {userDevice.serialNumber}
                        </SimpleButton>
                      </div>
                      {shouldShowDeviceStatusWrapper && (
                        <div className="device-status-wrapper">
                          <div className="item-status-wrapper">
                            <p className={userDevice.registered ? 'is-registered' : 'not-registered'}>
                              {userDevice.registered ? t('device_context.online_access_active') : t('device_context.online_access_inactive')}
                            </p>
                          </div>
                          <div className="item-button-wrapper">
                            <SimpleButton
                              className={userDevice.registered ? 'cancel-button' : 'action-button'}
                              onClick={() => handleChangeAssociation(userDevice.serialNumber, userDevice.userEmail, !userDevice.registered)}
                              requiredPermission={productsWritePermission}
                            >
                              {userDevice.registered ? t('device_context.unit.dissociate') : t('device_context.unit.associate')}
                            </SimpleButton>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              <div className="separator" />
              <div className="subscriptions-header" onClick={() => setAreSubscriptionsExpanded((prev) => !prev)}>
                <div className="header-title">
                  <div className="title">{t('nav.subscriptions')}</div>
                  <div className="subtitle">
                    {!isFirstSubscriptionsLoad && !isFirstDevicesLoad && <span className="highlight">{userSubscriptions.length}</span>}
                  </div>
                </div>
                <div className="expand-button">{areSubscriptionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</div>
              </div>
              {areSubscriptionsExpanded &&
                userSubscriptions.map((userSubscription, index) => (
                  <div
                    className={
                      'item-wrapper' + (index === 0 ? ' first-item-wrapper' : '') + (index === userSubscriptions.length - 1 ? ' last-item-wrapper' : '')
                    }
                    key={index}
                  >
                    <div className="item-image-wrapper">
                      <img src={userSubscription.product?.images[0]?.url || no_image_200x200} className="item-image" alt="product" />
                    </div>
                    <div className="item-title-wrapper">
                      <p className="title">{userSubscription.product?.title}</p>
                      <p className="subtitle">{userSubscription.product?.subtitle}</p>
                    </div>
                    <div className="subscription-status-wrapper">
                      <div className="item-status-wrapper">
                        <p className={`without-margins ${userSubscription.status}`}>
                          {t([`coltene_store.subscription_history.statuses.${userSubscription.status}`, userSubscription.status])}
                        </p>
                      </div>
                      <div className="item-button-wrapper">
                        <SimpleButton
                          className={userSubscription.cancelAtPeriodEnd ? 'action-button' : 'cancel-button'}
                          onClick={() => {
                            setSubscriptionToChange({ subscriptionId: userSubscription.id, cancelAtPeriodEnd: userSubscription.cancelAtPeriodEnd })
                            setShowChangeSubscriptionConfirmationModal(true)
                          }}
                          requiredPermission={usersWritePermission}
                        >
                          {userSubscription.cancelAtPeriodEnd ? t('reactivate') : t('cancel')}
                        </SimpleButton>
                      </div>
                    </div>
                  </div>
                ))}
              <div className="separator" />
            </div>
          )}
        </div>
      </PageModal>
      {showPasswordResetConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowPasswordResetConfirmModal(false)}
          onAccept={() => handleUserResetPassword()}
          onCancel={() => setShowPasswordResetConfirmModal(false)}
          message={t('users-management.reset-password-confirm')}
          requiredPermission={usersWritePermission}
        >
          <div className="reset-password-tooltip">
            <p>{t('users-management.reset-password-extra')}</p>
          </div>
        </ConfirmationModal>
      )}
      {showVerifyUserModal && (
        <ConfirmationModal
          onToggle={() => setShowVerifyUserModal(false)}
          onAccept={() => handleVerifyUser()}
          onCancel={() => setShowVerifyUserModal(false)}
          message={t('users-management.verify-user-confirm')}
          requiredPermission={usersWritePermission}
        />
      )}
      {showChangeSubscriptionConfirmationModal && (
        <ConfirmationModal
          onToggle={() => setShowChangeSubscriptionConfirmationModal(false)}
          onAccept={() => {
            setShowChangeSubscriptionConfirmationModal(false)
            handleChangeSubscription()
          }}
          onCancel={() => setShowChangeSubscriptionConfirmationModal(false)}
          message={t('store_context.confirm_change_subscription')}
          requiredPermission={usersWritePermission}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ users, userPermissions, authedUser, accounts, branches }) {
  return {
    userDevices: users?.userDevices ?? [],
    userSubscriptions: users?.userSubscriptions ?? [],
    users: users?.users ?? [],
    endUserPermissions: users?.endUserPermissions ?? [],
    accounts: accounts?.accounts || [],
    userRolesOptions: accounts?.userRolesOptions || [],
    userPermissions,
    authedUser,
    adminEmail: authedUser?.email,
    isCurrentAdmin: authedUser?.userGroup === 'admin',
    userBranches: branches?.branchesByCompanyId || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadUserDevices,
        loadUserSubscriptions,
        loadUserPermissions,
        loadUserRolesOptions,
        loadBranchesByCompanyId,
        partiallyUpdateUser,
        clearUserDevices,
        clearUserSubscriptions,
        clearUserPermissions,
        reactivateSubscription,
        cancelSubscription,
        clearUserBranches,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(ViewEditUser)

